"use client";

import React, { useRef, useEffect, useCallback } from "react";
import { Plate } from "@udecode/plate-common";
import type { TElement } from "@udecode/plate-common";
import { TooltipProvider } from "@/components/cms/ContentEditor/components/Tooltip";
import { cn } from "@udecode/cn";
import { CursorOverlay } from "@/components/cms/ContentEditor/components/CursorOverlay";
import { Editor } from "@/components/cms/ContentEditor/components/Editor";
import { FixedToolbar } from "@/components/cms/ContentEditor/components/FixedToolbar";
import { FixedToolbarButtons } from "@/components/cms/ContentEditor/components/FixedToolbarButtons";
import { Transforms } from "slate";
import type { BaseEditor } from "slate";
import { useEditorRef } from "@udecode/plate-core";
import { plugins } from "@/components/cms/ContentEditor/lib/plate-plugins";
import { debounce } from "@/utils";

function AutoFocus() {
	const editor = useEditorRef();
	useEffect(() => {
		setTimeout(() => {
			Transforms.select(editor as BaseEditor, { offset: 0, path: [0, 0] });
		});
	}, [editor]);
	return null;
}

function isEmptyValue(value: TElement[]) {
	// elements can contain a random ID
	// except the id an empty value is this: [{"type":"p","children":[{"text":""}]}]
	return (
		value.length === 1 &&
		value[0].type === "p" &&
		value[0].children.length === 1 &&
		value[0].children[0].text === ""
	);
}

/**
 * WYSIWYG content content-editor for the CMS
 * @params onChange
 * @constructor
 */
export default function ContentEditor(props: {
	readonly onChange: (value: TElement[] | "") => void;
	readonly value?: any;
}) {
	const editorContentRef = useRef(null);
	const firstOnChangeRef = useRef(true);

	const onChange = useCallback(
		(value: TElement[]) => {
			if (firstOnChangeRef.current) {
				// component triggers an onChange when render has finished
				// we are skipping this
				firstOnChangeRef.current = false;
				return;
			}

			if (typeof props.onChange === "function") {
				props.onChange(isEmptyValue(value) ? "" : value);
			}
		},

		[props]
	);

	const debouncedOnChange = debounce(onChange, 1000);

	return (
		<section className="rte-content w-full border border-gray-200">
			<TooltipProvider
				disableHoverableContent
				delayDuration={500}
				skipDelayDuration={0}
			>
				<Plate
					plugins={plugins}
					value={props.value}
					onChange={debouncedOnChange}
				>
					<div
						ref={editorContentRef}
						className={cn(
							"relative",
							// Block selection
							"[&_.slate-start-area-left]:!w-[64px] [&_.slate-start-area-right]:!w-[64px] [&_.slate-start-area-top]:!h-4"
						)}
					>
						<FixedToolbar>
							<FixedToolbarButtons />
						</FixedToolbar>

						<Editor
							autoFocus
							className="hmc-body-m px-[96px] py-16"
							focusRing={false}
							variant="ghost"
							size="md"
						/>

						<CursorOverlay containerRef={editorContentRef} />
						<AutoFocus />
					</div>
				</Plate>
			</TooltipProvider>
		</section>
	);
}
